import { CommerceAsset } from "./commerce-asset";
import { ClientStorage } from "./client-storage";
import { v4 as uuid } from "uuid";
import { assetActionTypes, cartTypes } from "../constants";
import { installBMPResponseInterceptor } from "../interceptors/bmp-response-interceptor";
import {
  addGiftcardToBagMutation,
  addToBagMutation,
  fetchCartAssetsQuery,
  getOosInfoQuery,
  refreshBagStockQuery,
  removeFromMyBagMutation,
  removeItemFromOosMutation,
  saveGiftCardToProfileMutation,
  setShortPaypalBrainTreePaymentInfoMutation,
  updateBagItemMutation,
  updateBagItemSkuMutation
} from "../operations/bag/queries";
import { getAfterPayClientTokenMutation } from "../operations/afterpay/queries";
import {
  addGiftMessageMutation,
  addEstimatedDeliveryDateMutation,
  applyGiftCardMutation,
  applySavedGiftCardMutation,
  getEstimatedDeliveryDateQuery,
  migrateToAnonymousBagQuery,
  paymentSessionQuery,
  refreshCheckoutQuery,
  refreshBagQuery,
  removeFromBagMutation,
  removeGiftCardMutation,
  removeGiftMessageMutation,
  removePaymentMutation,
  setAfterPayBillingInfoMutation,
  setBillingInfoMutation,
  setLongPaypalBraintreeInfoMutation,
  setSavedBillingInfoMutation,
  setShippingInfoMutation,
  submitOrderMutation,
  updateAnonymousBillingInfoMutation,
  validateAddressMutation,
  checkoutQuery
} from "../operations/checkout/queries";

/**
 * BagClient class provides methods to manage shopping bag operations,
 * including syncing assets, retrieving bag IDs, and handling shopping lists.
 */
class BagClient {
  /**
   * BagClient is a class that provides methods to interact with the bag API.
   * This class adds the bag id to the variables of the GraphQL request,
   * handles the response, and manages the assets related to the bag.
   * Creates an instance of BagClient.
   * @param {Object} params - The parameters for the BagClient.
   * @param {Object} params.client - The GraphQL client for making requests.
   * @param {string} params.locale - The locale for the client.
   * @throws {Error} If the client or locale is not provided.
   */
  constructor({ client, locale }) {
    if (!client) throw new Error("Parameter 'client' is required");
    if (!locale) throw new Error("Parameter 'locale' is required");

    this.client = client;
    this.locale = locale;
    this.storage = new ClientStorage();
    this.assets = new CommerceAsset(this.storage, locale);

    // Install BMP response interceptor for automatic error handling
    this.bmpInterceptorId = installBMPResponseInterceptor(this.client);
  }

  /**
   * Ensures that the required assets for the bag are loaded.
   * This method checks if the necessary assets are available and loads them if they are not.
   *
   * @private
   * @async
   * @function _ensureAssetsLoaded
   * @returns {Promise<void>} Resolves when the assets are successfully loaded.
   * @throws {Error} If the assets fail to load.
   */
  async _ensureAssetsLoaded(assetType) {
    const assets = this.assets?.[assetType];
    if (!assets || assets?.length === 0) {
      try {
        await this.syncAssets();
      } catch (error) {
        console.warn(
          `BagClient: ${assetType} - syncAssets failed: ${error?.message}`
        );
      }
    }
  }

  /**
   * Retrieves the bag ID for a given type.
   * If the assets are not synced, it will sync them first.
   * @param {string} type - The type of the bag (e.g., cart type).
   * @returns {Promise<string>} The bag ID for the specified type.
   */
  async getBagId(type) {
    // check, if the asset exists in the client storage
    // cart asset is the mandatory for the bag operations, so checking cart asset
    await this._ensureAssetsLoaded("carts");
    return this.assets.getBagIdByType(type);
  }

  /**
   * Retrieves the shopping list ID for a given type.
   * @param {string} type - The type of the shopping list.
   * @returns {string} The shopping list ID for the specified type.
   */
  async getShoppingListId(type) {
    await this._ensureAssetsLoaded("shoppingLists");
    return this.assets.getShoppingListIdByType(type);
  }

  /**
   * Handles extension data by updating assets if provided.
   * @param {Object} extensions - The extension data.
   * @param {Array} extensions.assets - The assets to update.
   */
  _handleExtensionData(extensions) {
    if (!extensions || typeof extensions !== "object") return;
    const { assets } = extensions || {};
    if (!assets || !assets?.length) return;

    assets.forEach((asset) => {
      if (!asset || typeof asset !== "object") return;

      const { category, type, action, payload } = asset || {};

      switch (action) {
        case assetActionTypes.UPDATE:
          this.assets.updateAsset({ category, payload, type });
          break;

        case assetActionTypes.REMOVE:
          this.assets.removeAsset({ category, payload, type });
          break;

        case assetActionTypes.REMOVE_ALL:
          this.assets.removeAll();
          break;

        default:
          throw new Error("Unknown action type ", action);
      }
    });
  }

  /**
   * Extends the variables with the bag ID.
   * If no bag ID is provided, it retrieves the default bag ID based on the bag type.
   * For peak readiness: Using cookie-based cart management, localStorage fallback disabled.
   * TODO: Re-enable localStorage fallback after cookie migration is complete.
   * @param {Object} variables - The variables to extend.
   * @param {string} [variables.bagId] - The bag ID.
   * @param {string} [variables.bagType] - The type of the bag (e.g., cart type).
   * @returns {Promise<Object>} The extended variables with the bag ID.
   * @throws {Error} If an error occurs while retrieving the bag ID.
   */
  async _extendVariables(variables) {
    // get the bag id if it is present in variables, otherwise use the default bag id

    // TEMPORARY: For peak readiness - using cookie-based cart management only
    // TODO: Uncomment after cookie migration is complete
    // const { bagId, bagType } = variables || {};
    // const id = bagId || (await this.getBagId(bagType || cartTypes.DEFAULT));
    // return { ...variables, bagId: id };

    // Peak readiness: Return variables as-is, relying on cookie-based bagId
    return variables;
  }

  /**
   * Synchronizes assets by fetching them from the server.
   * Updates the local assets with the fetched data.
   * For peak readiness: Disabled to avoid redundant fetchCartAssetsByToken calls.
   * TODO: Re-enable after cookie migration is complete.
   * @returns {Promise<void>} Resolves when the assets are successfully synced.
   * @throws {Error} If the GraphQL request fails or returns errors.
   */
  async syncAssets() {
    // TEMPORARY: For peak readiness - disable redundant fetchCartAssetsByToken call
    // TODO: Uncomment after cookie migration is complete
    // const { data = null, errors } = await this.request({
    //   query: fetchCartAssetsQuery,
    //   variables: {}
    // });

    // if (errors) {
    //   throw new Error(`Error in fetchCartAssetsByToken, ${errors[0].message}`);
    // }

    // if (data) {
    //   const { fetchCartAssetsByToken: assets = [] } = data || {};
    //   assets?.forEach((asset) => {
    //     this.assets.updateAsset(asset);
    //   });
    // }

    // Peak readiness: No-op to avoid redundant GraphQL calls
    return Promise.resolve();
  }

  /**
   * Removes all existing assets from the local storage.
   * @throws {Error} If an error occurs while removing assets.
   */
  removeExistingAssets() {
    try {
      this.assets.removeAll();
    } catch (error) {
      throw new Error(`Error in removeExistingAssets, ${error?.message}`);
    }
  }

  /**
   * Extends the variables with the shopping list ID.
   * If no shopping list ID is provided, it retrieves the default shopping list ID.
   * For peak readiness: Using cookie-based cart management, localStorage fallback disabled.
   * TODO: Re-enable localStorage fallback after cookie migration is complete.
   * @param {Object} variables - The variables to extend.
   * @param {string} [variables.shoppingListId] - The shopping list ID.
   * @returns {Object} The extended variables with the shopping list ID.
   */
  async _extendShoppingListVariables(variables) {
    // TEMPORARY: For peak readiness - using cookie-based cart management only
    // TODO: Uncomment after cookie migration is complete
    // const { shoppingListId } = variables || {};
    // const id =
    //   shoppingListId || (await this.getShoppingListId(cartTypes.DEFAULT));
    // return { ...variables, shoppingListId: id };

    // Peak readiness: Return variables as-is, relying on cookie-based shoppingListId
    return variables;
  }

  // async request({ query, variables, errorData }) {
  async request({ query, variables, ...rest }) {
    /**
     * Makes a GraphQL request with the provided query and variables.
     * @param {string} query - The GraphQL query to execute.
     * @param {Object} variables - The variables to use in the query.
     * @returns {Promise<Object>} The response data from the GraphQL request.
     * @throws {Error} If the 'client' parameter is not provided.
     */
    const requestCorrelationId = uuid();

    if (!this.client) throw new Error("Parameter 'client' is required");

    const headers = {
      "Content-Type": "application/json",
      "x-lll-client-repo-name": "guest-checkout-app",
      "x-lll-request-correlation-id": requestCorrelationId,
      "x-lll-client-commit": process.env.CI_COMMIT_SHA || ""
    };

    let operationName = undefined;

    if (query) {
      // Regex to find the operation name
      const operationNameRegex =
        /\b(?:query|mutation|subscription)\s+(?:\w+\s+)?(\w+)/;
      // Search for the match
      const match = query.match(operationNameRegex);

      if (match?.[1]) {
        operationName = match[1];
      }
    }

    const response = await this.client.graphql({
      data: { operationName, query, variables: variables, ...rest },
      headers
    });
    const { extensions, ...data } = response.data;

    this._handleExtensionData(extensions);
    return data;
  }

  /**
   * Adds an item to the bag.
   * Extends the variables with the bag ID and sends a GraphQL mutation.
   * @param {Object} variables - The variables for the add-to-bag mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   */
  async addToBag({ variables, errorData }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: addToBagMutation,
      variables: extendedVariables,
      errorData
    });
  }

  /**
   * Updates an item in the bag.
   * Extends the variables with the bag ID and sends a GraphQL mutation.
   * @param {Object} variables - The variables for the update-bag-item mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   */
  async updateBagItem({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: updateBagItemMutation,
      variables: extendedVariables
    });
  }

  /**
   * Adds a gift card to the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for adding the gift card.
   * @param {Object} params.variables - The variables for the add-gift-card-to-bag mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async addGiftCardToBag({ variables, errorData }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: addGiftcardToBagMutation,
      variables: extendedVariables,
      errorData
    });
  }

  /**
   * Refreshes the stock information for the bag by sending a GraphQL query.
   * @param {Object} params - The parameters for refreshing the bag stock.
   * @param {Object} params.variables - The variables for the refresh-bag-stock query.
   * @returns {Promise<Object>} The response data containing the refreshed stock information.
   * @throws {Error} If the request fails.
   */
  async refreshBagStock({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: refreshBagStockQuery,
      variables: extendedVariables
    });
  }

  /**
   * Retrieves the Afterpay client token by sending a GraphQL query.
   * @param {Object} params - The parameters for retrieving the Afterpay client token.
   * @param {Object} params.variables - The variables for the get-afterpay-client-token query.
   * @returns {Promise<Object>} The response data containing the Afterpay client token.
   * @throws {Error} If the request fails.
   */
  async getAfterpayClientToken({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: getAfterPayClientTokenMutation,
      variables: extendedVariables
    });
  }

  /**
   * Migrates the bag to an anonymous user by sending a GraphQL query.
   * @param {Object} params - The parameters for migrating the bag.
   * @param {Object} params.variables - The variables for the migrate-to-anonymous-bag query.
   * @returns {Promise<Object>} The response data from the query.
   * @throws {Error} If the request fails.
   */
  async migrateToAnonymousBag({ variables, req, res, getSecret }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: migrateToAnonymousBagQuery,
      variables: extendedVariables,
      req,
      res,
      getSecret
    });
  }

  /**
   * Sets AfterPay billing information for the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for setting the AfterPay billing information.
   * @param {Object} params.variables - The variables for the set-afterpay-billing-info mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async setAfterPayBillingInfo({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: setAfterPayBillingInfoMutation,
      variables: extendedVariables
    });
  }

  /**
   * Refreshes the bag during checkout by sending a GraphQL mutation.
   * @param {Object} params - The parameters for refreshing the bag.
   * @param {Object} params.variables - The variables for the refresh-checkout mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async checkoutRefreshBag({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: refreshCheckoutQuery,
      variables: extendedVariables
    });
  }

  /**
   * Refreshes the bag with optional EDD-only refresh by sending a GraphQL mutation.
   * @param {Object} params - The parameters for refreshing the bag.
   * @param {Object} params.variables - The variables for the refresh-bag mutation.
   * @param {string} [params.variables.bagId] - The bag ID to refresh.
   * @param {boolean} [params.variables.refreshEDDOnly=false] - Whether to refresh only EDD or all bag data.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async refreshBag({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: refreshBagQuery,
      variables: extendedVariables
    });
  }

  /**
   * Retrieves checkout page data by sending a GraphQL mutation.
   * @param {Object} params - The parameters for refreshing the bag.
   * @param {Object} params.variables - The variables for the refresh-checkout mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async checkoutQuery({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: checkoutQuery,
      variables: extendedVariables
    });
  }

  /**
   * Retrieves the estimated delivery date for the bag by sending a GraphQL query.
   * @param {Object} params - The parameters for retrieving the estimated delivery date.
   * @param {Object} params.variables - The variables for the get-estimated-delivery-date query.
   * @returns {Promise<Object>} The response data from the query.
   * @throws {Error} If the request fails.
   */
  async getEstimatedDeliveryDate({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: getEstimatedDeliveryDateQuery,
      variables: extendedVariables
    });
  }

  /**
   * Adds an estimated delivery date to the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for adding the estimated delivery date.
   * @param {Object} params.variables - The variables for the add-estimated-delivery-date mutation.
   * @param {string} params.variables.bagId - The bag ID.
   * @param {string} params.variables.estimatedDeliveryDate - The estimated delivery date to add.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async addEstimatedDeliveryDate({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: addEstimatedDeliveryDateMutation,
      variables: extendedVariables
    });
  }

  /**
   * Sets shipping information for the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for setting the shipping information.
   * @param {Object} params.variables - The variables for the set-shipping-info mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async setShippingInfo({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: setShippingInfoMutation,
      variables: extendedVariables
    });
  }

  /**
   * Applies a gift card to the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for applying the gift card.
   * @param {Object} params.variables - The variables for the apply-gift-card mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async applyGiftCard({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: applyGiftCardMutation,
      variables: extendedVariables
    });
  }

  async saveGiftCardToProfile({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: saveGiftCardToProfileMutation,
      variables: extendedVariables
    });
  }

  /**
   * Applies a saved gift card to the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for applying the saved gift card.
   * @param {Object} params.variables - The variables for the apply-saved-gift-card mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async applySavedGiftCard({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: applySavedGiftCardMutation,
      variables: extendedVariables
    });
  }

  /**
   * Removes a gift card from the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for removing the gift card.
   * @param {Object} params.variables - The variables for the remove-gift-card mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async removeGiftCard({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: removeGiftCardMutation,
      variables: extendedVariables
    });
  }

  /**
   * Adds a gift message to the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for adding the gift message.
   * @param {Object} params.variables - The variables for the add-gift-message mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async addGiftMessage({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: addGiftMessageMutation,
      variables: extendedVariables
    });
  }

  /**
   * Removes a gift message from the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for removing the gift message.
   * @param {Object} params.variables - The variables for the remove-gift-message mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async removeGiftMessage({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: removeGiftMessageMutation,
      variables: extendedVariables
    });
  }

  /**
   * Sets saved billing information by sending a GraphQL mutation.
   * @param {Object} params - The parameters for setting the saved billing information.
   * @param {Object} params.variables - The variables for the set-saved-billing-info mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async setSavedBillingInfo({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: setSavedBillingInfoMutation,
      variables: extendedVariables
    });
  }

  /**
   * Updates anonymous billing information by sending a GraphQL mutation.
   * @param {Object} params - The parameters for updating the billing information.
   * @param {Object} params.variables - The variables for the update-anonymous-billing-info mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async updateAnonymousBillingInfo({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: updateAnonymousBillingInfoMutation,
      variables: extendedVariables
    });
  }

  /**
   * Removes a payment method from the bag by sending a GraphQL mutation.
   * @param {Object} params - The parameters for removing the payment method.
   * @param {Object} params.variables - The variables for the remove-payment mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async removePayment({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: removePaymentMutation,
      variables: extendedVariables
    });
  }

  /**
   * Sets long PayPal Braintree billing information by sending a GraphQL mutation.
   * @param {Object} params - The parameters for setting the billing information.
   * @param {Object} params.variables - The variables for the set-long-paypal-braintree-info mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async setLongPaypalBraintreeInfoMutation({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: setLongPaypalBraintreeInfoMutation,
      variables: extendedVariables
    });
  }

  /**
   * Submits an order by sending a GraphQL mutation.
   * @param {Object} params - The parameters for submitting the order.
   * @param {Object} params.variables - The variables for the submit-order mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async setShortPaypalBrainTreePaymentInfo({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: setShortPaypalBrainTreePaymentInfoMutation,
      variables: extendedVariables
    });
  }

  async submitOrder({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: submitOrderMutation,
      variables: extendedVariables
    });
  }

  /**
   * Validates an address by sending a GraphQL mutation.
   * @param {Object} params - The parameters for the address validation.
   * @param {Object} params.variables - The variables for the validate-address mutation.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async validateAddress({ variables, req }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: validateAddressMutation,
      variables: extendedVariables,
      req
    });
  }

  /**
   * Initiates a payment session.
   * Extends the variables with the bag ID and sends a GraphQL query.
   * @param {Object} params - The parameters for the payment session.
   * @param {Object} params.variables - The variables for the payment session query.
   * @returns {Promise<Object>} The response data from the payment session query.
   * @throws {Error} If the request fails.
   */
  async paymentSession({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: paymentSessionQuery,
      variables: extendedVariables
    });
  }

  /**
   * Updates the SKU of an item in the bag.
   * Extends the variables with the bag ID and sends a GraphQL mutation.
   * @param {Object} params - The parameters for updating the bag item SKU.
   * @param {Object} params.variables - The variables for the update-bag-item-SKU mutation.
   * @param {Object} [params.errorData] - Optional error data to include in the request.
   * @returns {Promise<Object>} The response data from the mutation.
   * @throws {Error} If the request fails.
   */
  async updateBagItemSku({ variables, errorData }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: updateBagItemSkuMutation,
      variables: extendedVariables,
      errorData
    });
  }

  /**
   * Removes an out-of-stock item from the bag.
   * Extends the variables with the shopping list ID and sends a GraphQL mutation.
   * @param {Object} variables - The variables for the remove-OOS-item mutation.
   * @param {Object} [errorData] - Optional error data to include in the request.
   * @returns {Promise<Object>} The response data from the mutation.
   */
  async removeOosItem({ variables, errorData }) {
    const extendedVariables = await this._extendShoppingListVariables(
      variables
    );
    return this.request({
      query: removeItemFromOosMutation,
      variables: {
        ...extendedVariables
      },
      errorData
    });
  }

  /**
   * Retrieves out-of-stock (OOS) information for the given variables.
   * Sends a GraphQL query to fetch OOS information.
   * @param {Object} variables - The variables for the OOS query.
   * @returns {Promise<Object>} The response data containing OOS information.
   * @throws {Error} If the GraphQL request fails or returns errors.
   */
  async getOosInfo({ variables }) {
    const extendedVariables = await this._extendShoppingListVariables(
      variables
    );
    return this.request({
      query: getOosInfoQuery,
      variables: {
        ...extendedVariables
      }
    });
  }

  async checkoutRemoveFromBag({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: removeFromBagMutation,
      variables: {
        ...extendedVariables
      }
    });
  }

  async removeFromMyBag({ variables, errorData }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: removeFromMyBagMutation,
      variables: {
        ...extendedVariables
      },
      errorData
    });
  }

  async setBillingInfo({ variables }) {
    const extendedVariables = await this._extendVariables(variables);
    return this.request({
      query: setBillingInfoMutation,
      variables: {
        ...extendedVariables
      }
    });
  }
}

export default BagClient;
